import * as Debugs from "../Debugs";
import { OrbitControlCameraTupleMapper } from "./OrbitControlCameraTupleMapper";
import { CameraTupleHelper } from "./CameraTupleHelper";
import { CameraPathRunner } from "./CameraPathRunner";
import { CameraPath, ease, easeInOutSine, BezierFunction } from "./CameraPath";
import { v2, v3 } from "../short3";
import * as Maths2 from "../Maths2";
import * as Maths3 from "../Maths3";
import numeric from "numeric";
import { CameraTuple } from "./CameraTuple";

import * as _ from "lodash";
import { BufferGeometry, Line, LineBasicMaterial, Object3D, Vector2, Vector3 } from "three";

interface ICustomPath {
  getAt(t: number): CameraTuple;
  isLinear: boolean;
}

type SplineFn = (t: number) => number;
type VectorFn = (t: number) => Vector3;

/**
 * Erlaubt sanfte Kamerafahrten um ein Object herum oder zu vorbestimmten Pfaden
 * @example
 * const pathHelper = new CameraPathHelper();
 * const path = pathHelper.rotateAround(60*Math.PI/180,1,70*Math.PI/180);
 * pathHelper.showForDebug(path);
 * pathHelper.run(path);
 */
export class CameraPathHelper {
  cameraMapper: OrbitControlCameraTupleMapper;
  cameraTupleHelper: CameraTupleHelper;

  constructor() {
    this.cameraMapper = new OrbitControlCameraTupleMapper();
    this.cameraTupleHelper = new CameraTupleHelper();
  }

  set(cameraTuple: CameraTuple): void {
    this.cameraMapper.setCurrentCamera(cameraTuple);
  }

  linearPathTo(cameraTuple: CameraTuple, wayFunction: BezierFunction = easeInOutSine): CameraPath {
    const path = new CameraPath();
    path.add(this.cameraMapper.getCurrentCamera());
    path.add(cameraTuple);
    path.finalize(1000, wayFunction);
    return path;
  }

  dronePathTo(
    cameraTuple: CameraTuple,
    middleHeight: number,
    wayFunction: BezierFunction = easeInOutSine,
  ): CameraPath | ICustomPath {
    const start = this.cameraMapper.getCurrentCamera();
    const end = cameraTuple;
    const startDirection = start.getDirectionToTarget();
    const endDirection = end.getDirectionToTarget();
    const angle = startDirection.angleTo(endDirection);
    const nearlyColinear = angle < (45 * Math.PI) / 180;
    if (nearlyColinear) {
      const path = this.linearPathTo(cameraTuple, wayFunction);
      path.isLinear = true;
      return path;
    }
    const atT =
      <T>(spline: { at: (t: number) => T }): ((t: number) => T) =>
      (t: number): T =>
        spline.at(t);
    const compose =
      <A, B, C>(f: (b: B) => C, g: (a: A) => B): ((a: A) => C) =>
      (x: A): C =>
        f(g(x));
    const linearTargetFn = (t: number): Vector3 => v3().lerpVectors(start.target, end.target, t);
    const targetFn = compose(
      linearTargetFn,
      atT<number>(numeric.spline([0, 0.1, 0.2, 0.3, 1], [0, 0.8, 0.9, 1, 1])),
    );
    const upFn = (t: number): Vector3 => v3().lerpVectors(start.up, end.up, t);
    const yFn = atT<number>(
      numeric.spline(
        [0, 0.5, 0.7, 1],
        [start.position.y, middleHeight, middleHeight, end.position.y],
      ),
    );
    const xzFn = ((): ((t: number) => number[]) => {
      const toV2 = (v: Vector3): Vector2 => v2(v.x, v.z);
      const a = toV2(start.position);
      const b = toV2(end.position);
      const bToA = b.clone().sub(a);
      const sd = toV2(startDirection);
      const orthogonal = (bToA.cross(sd) < 0 ? Maths2.orthogonalRight : Maths2.orthogonalLeft)(
        bToA,
      );
      const basis = (vBasis: Vector2): Vector2 =>
        a
          .clone()
          .add(bToA.clone().multiplyScalar(vBasis.x))
          .add(orthogonal.clone().multiplyScalar(vBasis.y));
      const asArrays = (va: Vector2[]): number[][] =>
        va.map(basis).map((v: Vector2): number[] => v.toArray());
      return atT<number[]>(
        numeric.spline(
          [0, 0.4, 0.8, 1],
          asArrays([v2(0, 0), v2(0.5, 0.5), v2(1.1, 0.2), v2(1, 0)]),
        ),
      );
    })();
    return {
      getAt(t: number): CameraTuple {
        const target = targetFn(t);
        const y = yFn(t);
        const [x, z] = xzFn(t);
        const up = upFn(t);
        const position = v3(x, y, z);
        return new CameraTuple(position, target, up);
      },
      isLinear: false,
    };
  }

  rotateAround(
    model: Object3D,
    azimuth: number,
    timesAround: number = 1,
    endPolar: number | null = null,
    wayFunction: BezierFunction = easeInOutSine,
  ): CameraPath {
    const path = new CameraPath();
    path.add(this.cameraMapper.getCurrentCamera());
    const c = this.cameraTupleHelper.fitObjectAzimuth(model, azimuth);
    //path.add(c);
    const s = c.getSpherical();
    const start_p = s.y;
    const end_p =
      (endPolar != null ? Maths3.normalizeAngle(endPolar) : s.y) +
      (timesAround * 360 * Math.PI) / 180;
    for (
      let polar = start_p;
      polar < end_p;
      polar += Math.min((20 * Math.PI) / 180, end_p - polar)
    ) {
      s.y = polar;
      path.add(c.setSpherical(s));
    }
    s.y = end_p;
    path.add(c.setSpherical(s));
    //path.add(start);
    path.finalize(1000, wayFunction);
    return path;
  }

  run(path: CameraPath | ICustomPath, duration: number = 10000): Promise<void> | undefined {
    const runner = new CameraPathRunner(path as CameraPath, duration);
    return runner.start();
  }

  showForDebug(path: CameraPath, resolution: number = 300): void {
    const showLine = (curve: (t: number) => Vector3, color: number): void => {
      const geometry = new BufferGeometry().setFromPoints(
        _.range(0, 1, 1 / resolution).map((x: number): Vector3 => curve(x)),
      );
      const material = new LineBasicMaterial({ color });
      const line = new Line(geometry, material);
      Debugs.add(line);
      for (let t = 0; t < 1; t += 1 / resolution) {
        Debugs.point(curve(t), color);
      }
    };
    showLine(path.positionCurve, 0xff0000);
    showLine(path.targetCurve, 0x0000ff);
  }
}
